import type { SessionUser } from "@ap/ui-kit";

/** Decodes a JWT payload without verifying it (services verify; the UI only reads claims for UX). */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split(".")[1];
  if (!part) return {};
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Builds the session user from Keycloak access-token claims (realm roles, ap_tenant, ap_locale). */
export function userFromClaims(claims: Record<string, unknown>): SessionUser {
  const realm = claims.realm_access as { roles?: unknown } | undefined;
  const roles = Array.isArray(realm?.roles) ? realm.roles.filter((r): r is string => typeof r === "string") : [];
  const s = (k: string) => (typeof claims[k] === "string" ? (claims[k] as string) : undefined);
  return {
    sub: s("sub") ?? "",
    username: s("preferred_username") ?? s("sub") ?? "",
    name: s("name") ?? s("preferred_username") ?? "",
    email: s("email"),
    tenant: s("ap_tenant") ?? "",
    locale: s("ap_locale") ?? s("locale"),
    roles,
    attributes: attributesFromClaims(claims),
  };
}

/**
 * Work attributes a profile's placeholders may use (`{user.unit}`): every `ap_<name>` string claim
 * other than the tenant and locale, and the string values of an `ap_attributes` object claim.
 */
export function attributesFromClaims(claims: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(claims)) {
    if (k.startsWith("ap_") && k !== "ap_tenant" && k !== "ap_locale" && k !== "ap_attributes" && typeof v === "string") out[k.slice(3)] = v;
  }
  const extra = claims.ap_attributes;
  if (extra && typeof extra === "object" && !Array.isArray(extra)) for (const [k, v] of Object.entries(extra)) if (typeof v === "string" || typeof v === "number") out[k] = String(v);
  return out;
}
